import {
  type FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  type Api,
  ApiError,
  type GenerationBrief,
  type ManagedAstroImportAnalysis,
  type ManagedAstroImportRecord,
  type ManagedAstroImportStatus,
  type ManagedAstroPendingConfigQuestion,
  type ManagedAstroRunnerReport,
  type ManagedAstroSourceArtifact,
  type ManagedAstroSourceUploadFile,
  type ManagedAstroUploadFile,
  type MigrationJob,
  type MirrorFidelityCheck,
  previewUrlFor,
  type WordPressRestCmsEnrichment,
} from "../api";

const PHASE_LABEL: Record<string, string> = {
  capturing: "Reading your site",
  rendering: "Rendering JavaScript",
  publishing: "Preparing pages",
  enriching: "Importing your content",
};

const SKIPPED_SOURCE_DIRS = new Set([
  ".astro",
  ".cache",
  ".git",
  ".next",
  ".netlify",
  ".nuxt",
  ".output",
  ".parcel-cache",
  ".pnpm-store",
  ".sanity",
  ".svelte-kit",
  ".turbo",
  ".vercel",
  ".vite",
  ".wrangler",
  "build",
  "coverage",
  "dist",
  "out",
  "playwright-report",
  "test-results",
  "node_modules",
]);
const SKIPPED_SOURCE_FILES = new Set([
  ".DS_Store",
  ".env",
  ".env.local",
  ".env.production",
  ".env.staging",
  ".gitignore",
  "npm-debug.log",
  "pnpm-debug.log",
  "yarn-debug.log",
  "yarn-error.log",
]);
const TEXT_FILE_RE =
  /\.(astro|cjs|css|html|js|json|jsx|md|mdx|mjs|ndjson|toml|ts|tsx|txt|yaml|yml)$/i;
const TEXT_FILE_NAMES = new Set([
  "astro.config.cjs",
  "astro.config.js",
  "astro.config.mjs",
  "astro.config.ts",
  "bun.lock",
  "bun.lockb",
  "package-lock.json",
  "package.json",
  "pnpm-lock.yaml",
  "sanity.cli.js",
  "sanity.cli.ts",
  "sanity.config.js",
  "sanity.config.ts",
  "yarn.lock",
]);
const MAX_TEXT_FILE_BYTES = 1_000_000;
const MAX_TOTAL_TEXT_BYTES = 4_000_000;
const MAX_SANITY_EXPORT_BYTES = 50_000_000;
const MAX_SANITY_EXPORT_ANALYSIS_TEXT_BYTES = 1_900_000;
const MAX_MANAGED_ASTRO_UPLOAD_FILES = 2_000;
const MAX_GITIGNORE_BYTES = 256_000;
const MANAGED_ASTRO_IMPORT_POLL_INTERVAL_MS = 3_000;
const MANAGED_ASTRO_RUNNER_SCHEDULE_COPY =
  "Groundbeam checks queued builds automatically. The hosted runner can take up to 15 minutes to start, and this panel refreshes every few seconds.";

export interface PreparedManagedAstroUpload {
  files: ManagedAstroUploadFile[];
  skippedFiles: number;
  sourceFiles: ManagedAstroSourceUploadFile[];
  sourceLabel: string | null;
  textBytes: number;
  totalBytes: number;
  totalFiles: number;
}

export interface PreparedManagedAstroSanityExport {
  file: File;
  mimeType: string | null;
  originalName: string;
  path: string;
  sizeBytes: number;
  text: string | null;
}

interface RawManagedAstroUploadFile {
  file: File;
  path: string;
}

interface ManagedAstroDirectoryPickerWindow extends Window {
  showDirectoryPicker?: (options?: {
    mode?: "read" | "readwrite";
  }) => Promise<ManagedAstroDirectoryHandle>;
}

type ManagedAstroFileSystemHandle =
  | ManagedAstroDirectoryHandle
  | ManagedAstroFileHandle;

interface ManagedAstroDirectoryHandle {
  entries(): AsyncIterable<[string, ManagedAstroFileSystemHandle]>;
  kind: "directory";
  name: string;
}

interface ManagedAstroFileHandle {
  getFile(): Promise<File>;
  kind: "file";
  name: string;
}

interface GitignoreRule {
  anchored: boolean;
  baseDir: string;
  directoryOnly: boolean;
  hasSlash: boolean;
  negated: boolean;
  regex: RegExp;
}

export function MigratePanel({
  api,
  canGenerate = false,
  hostname,
  onImportComplete,
}: {
  api: Api;
  canGenerate?: boolean;
  hostname: string | null;
  onImportComplete?: (result: ManagedAstroImportRecord | MigrationJob) => void;
}) {
  const [url, setUrl] = useState("");
  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<MigrationJob | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fidelity, setFidelity] = useState<MirrorFidelityCheck | null>(null);
  const [wordPressEnrichment, setWordPressEnrichment] =
    useState<WordPressRestCmsEnrichment | null>(null);
  const [mode, setMode] = useState<"generate" | "url" | "source">("url");
  const [isExpanded, setIsExpanded] = useState(() =>
    migratePanelStartsExpanded(hostname),
  );
  // cache-bust the preview link so a re-import is visible immediately
  const [previewNonce, setPreviewNonce] = useState(0);
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishNote, setPublishNote] = useState("");
  const notifiedCompleteJobIds = useRef(new Set<string>());

  // Rehydrate the URL lane across page loads: an in-flight migration or a
  // gate-phased snapshot (awaiting checks / a publish decision) must survive
  // a reload — without this, a blocked site's publish button would be
  // unreachable after closing the tab.
  useEffect(() => {
    if (jobId) return;
    let cancelled = false;
    (async () => {
      try {
        const { jobs } = await api.listImports();
        if (cancelled) return;
        const latest = jobs[0];
        if (!latest) return;
        const inFlight =
          latest.status === "queued" || latest.status === "running";
        const gated =
          latest.status === "complete" && isUrlMigrationGatePhase(latest.phase);
        if (inFlight || gated) {
          setJobId(latest.id);
          setMode("url");
          setIsExpanded(true);
        }
      } catch {
        /* purely additive — a failed listing changes nothing */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, jobId]);

  // Poll the job until it reaches a terminal state, then keep polling (at
  // the same cadence, bounded) while the §15.3 quality checks run in the
  // hosted runner. The interval key is the job id only, so transient fetch
  // errors don't tear down the poller.
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    let pollsSinceComplete = 0;
    const poll = async () => {
      try {
        const status = await api.getImport(jobId);
        if (cancelled) return;
        const next = status.job;
        setJob(next);
        setFidelity(status.fidelity ?? null);
        setWordPressEnrichment(status.cmsEnrichment?.wordpressRest ?? null);
        if (next.status === "complete") {
          pollsSinceComplete += 1;
          if (
            shouldNotifyImportComplete(next, notifiedCompleteJobIds.current)
          ) {
            setPreviewNonce(Date.now());
            setIsExpanded(false);
            notifiedCompleteJobIds.current.add(next.id);
            onImportComplete?.(next);
          }
        }
        if (
          !shouldContinueImportPolling({
            fidelityStatus: status.fidelity?.status ?? null,
            jobStatus: next.status,
            phase: next.phase,
            pollsSinceComplete,
          })
        ) {
          clearInterval(timer);
        }
      } catch {
        /* transient - keep polling */
      }
    };
    const timer = setInterval(poll, 1600);
    poll();
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [api, jobId, onImportComplete]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setJob(null);
    setStarting(true);
    setIsExpanded(true);
    try {
      const { jobId: id } = await api.startImport(url.trim());
      setJobId(id);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not start the import.",
      );
    } finally {
      setStarting(false);
    }
  }

  // §15.3 gated publish: promote the preview snapshot to the live host. A
  // failed verdict requires a note (accept-with-note); incomplete checks
  // publish with an optional note.
  async function onGatedPublish(noteRequired: boolean) {
    if (!job) return;
    const note = publishNote.trim();
    if (noteRequired && !note) {
      setPublishError(
        "Add a short note saying why it's OK to publish despite the check results.",
      );
      return;
    }
    setPublishError(null);
    setPublishBusy(true);
    try {
      await api.publishMirrorImport(
        job.id,
        note ? { fidelityOverride: { note } } : undefined,
      );
      const status = await api.getImport(job.id);
      setJob(status.job);
      setFidelity(status.fidelity ?? null);
      setPreviewNonce(Date.now());
      setPublishNote("");
      if (
        shouldNotifyImportComplete(status.job, notifiedCompleteJobIds.current)
      ) {
        notifiedCompleteJobIds.current.add(status.job.id);
        setIsExpanded(false);
        onImportComplete?.(status.job);
      }
    } catch (err) {
      setPublishError(
        err instanceof ApiError ? err.message : "Could not publish the site.",
      );
    } finally {
      setPublishBusy(false);
    }
  }

  const running =
    job?.status === "queued" || job?.status === "running" || starting;
  const pct =
    job && job.pagesTotal > 0
      ? Math.round((job.pagesDone / job.pagesTotal) * 100)
      : null;
  const showCompactPanel = shouldShowCompactMigratePanel({
    hasError: Boolean(error),
    hostname,
    isExpanded,
    isRunning: running,
    jobStatus: job?.status ?? null,
  });

  if (showCompactPanel) {
    return (
      <section className="card migrate-card compact">
        <div className="migrate-compact">
          <div className="migrate-compact-copy">
            <h2>Website imported</h2>
            <p className="muted">
              {job?.status === "complete"
                ? `Migrated ${job.pagesDone} page${job.pagesDone === 1 ? "" : "s"}.`
                : "Your current site is ready to review and edit."}
            </p>
            <MirrorFidelitySummary check={fidelity} />
          </div>
          <div className="migrate-compact-actions">
            {hostname && (
              <a
                className="btn-secondary"
                href={`https://${hostname}?v=${previewNonce}`}
                target="_blank"
                rel="noreferrer"
              >
                View live
              </a>
            )}
            <button
              className="btn-secondary"
              data-testid="managed-astro-source-mode"
              onClick={() => {
                setError(null);
                setJob(null);
                setMode("url");
                setIsExpanded(true);
              }}
              type="button"
            >
              Replace site
            </button>
            <button
              className="btn-secondary"
              onClick={() => {
                setError(null);
                setJob(null);
                setMode("source");
                setIsExpanded(true);
              }}
              type="button"
            >
              Source import
            </button>
            {canGenerate && (
              <button
                className="btn-secondary"
                onClick={() => {
                  setError(null);
                  setJob(null);
                  setMode("generate");
                  setIsExpanded(true);
                }}
                type="button"
              >
                Generate
              </button>
            )}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="card migrate-card">
      <div className="card-head">
        <h2>{hostname ? "Migrate a website" : "Bring your website in"}</h2>
        <p className="muted">
          {hostname
            ? "Start with a public URL for a fast mirror, or upload an Astro project folder when you have the source and CMS content."
            : "Enter your current website's address. Groundbeam copies it pixel for pixel, makes it fast, and starts catching your leads."}
        </p>
      </div>

      <div
        className="import-mode-switch"
        role="tablist"
        aria-label="Import method"
      >
        <button
          aria-selected={mode === "url"}
          className={mode === "url" ? "active" : ""}
          onClick={() => setMode("url")}
          role="tab"
          type="button"
        >
          Website URL
        </button>
        <button
          aria-selected={mode === "source"}
          className={mode === "source" ? "active" : ""}
          data-testid="managed-astro-source-mode"
          onClick={() => setMode("source")}
          role="tab"
          type="button"
        >
          Astro source
        </button>
        {canGenerate && (
          <button
            aria-selected={mode === "generate"}
            className={mode === "generate" ? "active" : ""}
            data-testid="generate-site-mode"
            onClick={() => setMode("generate")}
            role="tab"
            type="button"
          >
            Generate
          </button>
        )}
      </div>

      {/* Both panels stay MOUNTED and toggle via `hidden`: switching tabs
          must never reset in-flight state (an import in progress, a typed
          URL, a prepared upload) — unmounting was exactly that bug. */}
      <div hidden={mode !== "url"}>
        <form className="migrate-form" onSubmit={onSubmit}>
          <input
            type="url"
            inputMode="url"
            placeholder="https://your-old-site.com"
            value={url}
            required
            disabled={running}
            onChange={(e) => setUrl(e.target.value)}
          />
          <button type="submit" disabled={running || url.trim().length === 0}>
            {running ? "Migrating..." : "Migrate"}
          </button>
        </form>

        {error && <p className="error">{error}</p>}

        {job && (
          <div className="job">
            {running && (
              <div
                className="source-runner-progress"
                data-testid="url-migration-progress"
              >
                <div className="source-runner-progress-head">
                  <span aria-hidden="true" className="source-runner-pulse" />
                  <div>
                    <strong>
                      {PHASE_LABEL[job.phase ?? ""] ?? "Working on it"}
                    </strong>
                    <p className="muted small">
                      {job.pagesTotal > 0
                        ? `${job.pagesDone} of ${job.pagesTotal} pages`
                        : "This usually takes a minute or two."}
                    </p>
                  </div>
                </div>
                <ol
                  className="source-runner-steps url-migration-steps"
                  aria-label="Migration progress"
                >
                  {urlMigrationSteps({
                    fidelityReportStatus: fidelity?.report?.status ?? null,
                    fidelityStatus: fidelity?.status ?? null,
                    jobStatus: job.status,
                    phase: job.phase,
                  }).map((step) => (
                    <li className={step.state} key={step.label}>
                      <span />
                      {step.label}
                    </li>
                  ))}
                </ol>
                <div className="progress">
                  <div
                    className="progress-bar"
                    style={{ width: `${pct ?? 8}%` }}
                  />
                </div>
              </div>
            )}

            {job.status === "complete" && (
              <div className="job-done">
                <p className="success">
                  Migrated {job.pagesDone} page
                  {job.pagesDone === 1 ? "" : "s"}
                  {job.needsRendering > 0
                    ? ` (${job.needsRendering} need JavaScript rendering)`
                    : ""}
                  .
                </p>
                {(() => {
                  const wpLine = wordPressEnrichmentLine(wordPressEnrichment);
                  return wpLine ? (
                    <p className="muted small">{wpLine}</p>
                  ) : null;
                })()}
                <MirrorFidelitySummary check={fidelity} />
                {(() => {
                  const previewHref = job.hostname
                    ? `${previewUrlFor(job.hostname)}?v=${previewNonce}`
                    : null;
                  if (job.phase === "quality_checks") {
                    return (
                      <>
                        <p className="muted small">
                          Your site goes live automatically when the checks
                          pass. You can look at the preview meanwhile.
                        </p>
                        {previewHref && (
                          <a
                            className="btn-secondary"
                            href={previewHref}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Preview your site
                          </a>
                        )}
                      </>
                    );
                  }
                  const blocked = job.phase === "publish_blocked";
                  if (blocked || job.phase === "checks_incomplete") {
                    return (
                      <div
                        className="mirror-publish-gate"
                        data-testid="mirror-publish-gate"
                      >
                        <p className={blocked ? "error small" : "muted small"}>
                          {blocked
                            ? "Quality checks found problems, so your site is not live yet. Review the preview — you can still publish with a note."
                            : "Quality checks could not finish. Review the preview and publish when you're ready."}
                        </p>
                        {previewHref && (
                          <a
                            className="btn-secondary"
                            href={previewHref}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Preview your site
                          </a>
                        )}
                        <textarea
                          className="mirror-publish-note"
                          onChange={(e) => setPublishNote(e.target.value)}
                          placeholder={
                            blocked
                              ? "Why is it OK to publish? (required)"
                              : "Note (optional)"
                          }
                          rows={2}
                          value={publishNote}
                        />
                        <button
                          className="btn-primary"
                          data-testid="mirror-publish-anyway"
                          disabled={publishBusy}
                          onClick={() => onGatedPublish(blocked)}
                          type="button"
                        >
                          {publishBusy
                            ? "Publishing..."
                            : blocked
                              ? "Publish anyway"
                              : "Publish site"}
                        </button>
                        {publishError && (
                          <p className="error small">{publishError}</p>
                        )}
                      </div>
                    );
                  }
                  return hostname ? (
                    <a
                      className="btn-primary"
                      href={`https://${hostname}?v=${previewNonce}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View your live site
                    </a>
                  ) : null;
                })()}
              </div>
            )}

            {job.status === "failed" && (
              <p className="error">
                Migration failed{job.error ? `: ${job.error}` : "."}
              </p>
            )}
          </div>
        )}
      </div>
      <div hidden={mode !== "source"}>
        <ManagedAstroSourceImport
          api={api}
          onImportComplete={onImportComplete}
        />
      </div>
      {canGenerate && (
        <div hidden={mode !== "generate"}>
          <GenerateSiteFlow api={api} onImportComplete={onImportComplete} />
        </div>
      )}
    </section>
  );
}

const EMPTY_GENERATION_BRIEF: GenerationBrief = {
  businessName: "",
  description: "",
  industry: "",
  tone: "",
  language: "en",
  goals: [],
  pagesHint: [],
  contact: { email: "", phone: "", address: "" },
};

function GenerateSiteFlow({
  api,
  onImportComplete,
}: {
  api: Api;
  onImportComplete?: (record: ManagedAstroImportRecord) => void;
}) {
  const [brief, setBrief] = useState(EMPTY_GENERATION_BRIEF);
  const [goalsText, setGoalsText] = useState("");
  const [pagesText, setPagesText] = useState("");
  const [importRecord, setImportRecord] =
    useState<ManagedAstroImportRecord | null>(null);
  const [buildReport, setBuildReport] =
    useState<ManagedAstroRunnerReport | null>(null);
  const [workingStep, setWorkingStep] = useState<
    "generating" | "loading-report" | "publishing" | "refreshing-import" | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [lastCheckedAt, setLastCheckedAt] = useState<string | null>(null);
  const notifiedReadyImportIds = useRef(new Set<string>());
  const pollImportId =
    importRecord && shouldPollGenerateImport(importRecord.status)
      ? importRecord.id
      : null;

  const notifyImportReady = useCallback(
    (record: ManagedAstroImportRecord) => {
      if (
        shouldNotifyManagedAstroImportReady(
          record,
          notifiedReadyImportIds.current,
        )
      ) {
        notifiedReadyImportIds.current.add(record.id);
        onImportComplete?.(record);
      }
    },
    [onImportComplete],
  );

  useEffect(() => {
    if (!pollImportId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const result = await api.getManagedAstroImport(pollImportId);
        if (!cancelled) {
          setImportRecord(result.import);
          setLastCheckedAt(new Date().toISOString());
          notifyImportReady(result.import);
        }
      } catch {
        // Keep polling while generation and the hosted build are active.
      }
    };
    const timer = setInterval(poll, MANAGED_ASTRO_IMPORT_POLL_INTERVAL_MS);
    void poll();
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [api, notifyImportReady, pollImportId]);

  function updateBrief<K extends keyof GenerationBrief>(
    key: K,
    value: GenerationBrief[K],
  ) {
    setBrief((current) => ({ ...current, [key]: value }));
  }

  function updateContact(key: "email" | "phone" | "address", value: string) {
    setBrief((current) => ({
      ...current,
      contact: { ...current.contact, [key]: value },
    }));
  }

  async function generate(event: FormEvent) {
    event.preventDefault();
    setWorkingStep("generating");
    setError(null);
    setBuildReport(null);
    setImportRecord(null);
    try {
      const compact = (value: string | undefined) => value?.trim() || undefined;
      const contact = {
        email: compact(brief.contact?.email),
        phone: compact(brief.contact?.phone),
        address: compact(brief.contact?.address),
      };
      const result = await api.startGeneration({
        businessName: brief.businessName.trim(),
        description: brief.description.trim(),
        industry: compact(brief.industry),
        tone: compact(brief.tone),
        language: compact(brief.language) ?? "en",
        goals: goalsText
          .split("\n")
          .map((value) => value.trim())
          .filter(Boolean),
        pagesHint: pagesText
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean),
        contact: Object.values(contact).some(Boolean) ? contact : undefined,
      });
      const record = await api.getManagedAstroImport(result.importId);
      setImportRecord(record.import);
      setLastCheckedAt(new Date().toISOString());
      notifyImportReady(record.import);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not start your site.",
      );
    } finally {
      setWorkingStep(null);
    }
  }

  async function refreshImport() {
    if (!importRecord) return;
    setWorkingStep("refreshing-import");
    setError(null);
    try {
      const result = await api.getManagedAstroImport(importRecord.id);
      setImportRecord(result.import);
      setLastCheckedAt(new Date().toISOString());
      notifyImportReady(result.import);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not refresh your site.",
      );
    } finally {
      setWorkingStep(null);
    }
  }

  async function loadBuildReport() {
    if (!importRecord) return;
    setWorkingStep("loading-report");
    setError(null);
    try {
      const result = await api.getManagedAstroBuildReport(importRecord.id);
      setBuildReport(result.report);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not load the build report.",
      );
    } finally {
      setWorkingStep(null);
    }
  }

  async function publishImport() {
    if (!importRecord) return;
    setWorkingStep("publishing");
    setError(null);
    try {
      const result = await api.publishManagedAstroImport(importRecord.id);
      setImportRecord(result.import);
      notifyImportReady(result.import);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not publish your site.",
      );
    } finally {
      setWorkingStep(null);
    }
  }

  if (importRecord) {
    const steps = generateImportSteps({
      status: importRecord.status,
      buildCommand: importRecord.build?.command ?? null,
    });
    const ready = ["preview_ready", "publish_ready", "published"].includes(
      importRecord.status,
    );
    return (
      <div className="source-import-flow">
        <div
          className="source-runner-progress"
          data-testid="generate-site-progress"
        >
          <div className="source-runner-progress-head">
            <span
              aria-hidden="true"
              className={
                shouldPollGenerateImport(importRecord.status)
                  ? "source-runner-pulse"
                  : "source-runner-pulse idle"
              }
            />
            <div>
              <strong>
                {importRecord.status === "analysis_ready"
                  ? "Writing your site"
                  : "Preparing your site"}
              </strong>
              {importRecord.status === "analysis_ready" && (
                <p className="muted small">
                  The model is writing your site from the brief — this usually
                  takes a minute or two.
                </p>
              )}
            </div>
          </div>
          <ol
            className="source-runner-steps"
            aria-label="Site generation progress"
          >
            {steps.map((step) => (
              <li className={step.state} key={step.label}>
                <span />
                {step.label}
              </li>
            ))}
          </ol>
        </div>
        {importRecord.status === "failed" && (
          <>
            <p className="error">
              {importRecord.build?.error ?? "We could not create that site."}
            </p>
            <button
              className="source-analyze-button secondary"
              onClick={() => {
                setImportRecord(null);
                setBuildReport(null);
                setError(null);
              }}
              type="button"
            >
              Try again
            </button>
          </>
        )}
        {error && <p className="error">{error}</p>}
        {ready && (
          <ManagedAstroAnalysisReport
            analysis={importRecord.analysis}
            buildReport={buildReport}
            envConfigValues={{}}
            importRecord={importRecord}
            isLoadingReport={workingStep === "loading-report"}
            isPublishing={workingStep === "publishing"}
            isQueuing={false}
            isRefreshingImport={workingStep === "refreshing-import"}
            isResolvingEnv={false}
            lastCheckedAt={lastCheckedAt}
            onEnvConfigChange={() => {}}
            onLoadBuildReport={loadBuildReport}
            onPublish={publishImport}
            onQueueBuild={() => {}}
            onRefreshImport={refreshImport}
            onResolveEnvConfig={() => {}}
            sourceArtifact={null}
          />
        )}
      </div>
    );
  }

  return (
    <form className="source-import-flow" onSubmit={generate}>
      <div className="source-import-intro">
        <strong>Tell us about your business</strong>
        <p className="muted">
          Groundbeam will write a complete first version for you to review.
        </p>
      </div>
      <div className="source-env-grid">
        <label>
          <span>Business name</span>
          <input
            required
            maxLength={200}
            value={brief.businessName}
            onChange={(event) =>
              updateBrief("businessName", event.target.value)
            }
          />
        </label>
        <label>
          <span>Industry (optional)</span>
          <input
            maxLength={120}
            value={brief.industry}
            onChange={(event) => updateBrief("industry", event.target.value)}
          />
        </label>
        <label>
          <span>Tone (optional)</span>
          <input
            maxLength={120}
            value={brief.tone}
            onChange={(event) => updateBrief("tone", event.target.value)}
          />
        </label>
        <label>
          <span>Contact email (optional)</span>
          <input
            type="email"
            value={brief.contact?.email}
            onChange={(event) => updateContact("email", event.target.value)}
          />
        </label>
        <label>
          <span>Contact phone (optional)</span>
          <input
            type="tel"
            value={brief.contact?.phone}
            onChange={(event) => updateContact("phone", event.target.value)}
          />
        </label>
        <label>
          <span>Contact address (optional)</span>
          <input
            value={brief.contact?.address}
            onChange={(event) => updateContact("address", event.target.value)}
          />
        </label>
      </div>
      <label>
        <span>What does your business do?</span>
        <textarea
          required
          maxLength={4000}
          value={brief.description}
          onChange={(event) => updateBrief("description", event.target.value)}
        />
      </label>
      <label>
        <span>Goals (optional, one per line)</span>
        <textarea
          value={goalsText}
          onChange={(event) => setGoalsText(event.target.value)}
        />
      </label>
      <label>
        <span>Pages (optional, separated by commas)</span>
        <input
          placeholder="/, /services, /contact"
          value={pagesText}
          onChange={(event) => setPagesText(event.target.value)}
        />
      </label>
      <button
        className="source-analyze-button"
        disabled={
          workingStep !== null ||
          !brief.businessName.trim() ||
          !brief.description.trim()
        }
        type="submit"
      >
        {workingStep === "generating" ? "Starting..." : "Generate my site"}
      </button>
      {error && <p className="error">{error}</p>}
    </form>
  );
}

function ManagedAstroSourceImport({
  api,
  onImportComplete,
}: {
  api: Api;
  onImportComplete?: (record: ManagedAstroImportRecord) => void;
}) {
  const [prepared, setPrepared] = useState<PreparedManagedAstroUpload | null>(
    null,
  );
  const [analysis, setAnalysis] = useState<ManagedAstroImportAnalysis | null>(
    null,
  );
  const [importRecord, setImportRecord] =
    useState<ManagedAstroImportRecord | null>(null);
  const [sourceArtifact, setSourceArtifact] =
    useState<ManagedAstroSourceArtifact | null>(null);
  const [sanityExport, setSanityExport] =
    useState<PreparedManagedAstroSanityExport | null>(null);
  const [buildReport, setBuildReport] =
    useState<ManagedAstroRunnerReport | null>(null);
  const [workingStep, setWorkingStep] = useState<
    | "analyzing"
    | "loading-report"
    | "publishing"
    | "queuing"
    | "refreshing-import"
    | "resolving-env"
    | "uploading"
    | null
  >(null);
  const [envConfigValues, setEnvConfigValues] = useState<
    Record<string, string>
  >({});
  const [error, setError] = useState<string | null>(null);
  const notifiedReadyImportIds = useRef(new Set<string>());
  // Uploader folds away once an import exists (calm-by-default); collapse
  // only ONCE so the user's own toggle is never fought by the status poll.
  const [uploaderOpen, setUploaderOpen] = useState(true);
  const uploaderCollapsedOnce = useRef(false);
  const [managedAstroLastCheckedAt, setManagedAstroLastCheckedAt] = useState<
    string | null
  >(null);
  const directoryInputProps = { directory: "", webkitdirectory: "" };
  const directoryPicker =
    typeof window !== "undefined"
      ? (window as ManagedAstroDirectoryPickerWindow).showDirectoryPicker?.bind(
          window,
        )
      : undefined;
  const pollImportId = shouldPollManagedAstroImport(importRecord)
    ? (importRecord?.id ?? null)
    : null;

  useEffect(() => {
    if (
      importRecord &&
      !uploaderCollapsedOnce.current &&
      workingStep === null
    ) {
      uploaderCollapsedOnce.current = true;
      setUploaderOpen(false);
    }
  }, [importRecord, workingStep]);

  const notifyImportReady = useCallback(
    (record: ManagedAstroImportRecord) => {
      if (
        shouldNotifyManagedAstroImportReady(
          record,
          notifiedReadyImportIds.current,
        )
      ) {
        notifiedReadyImportIds.current.add(record.id);
        onImportComplete?.(record);
      }
    },
    [onImportComplete],
  );

  useEffect(() => {
    if (!pollImportId) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const result = await api.getManagedAstroImport(pollImportId);
        if (!cancelled) {
          setImportRecord(result.import);
          setManagedAstroLastCheckedAt(new Date().toISOString());
          notifyImportReady(result.import);
        }
      } catch {
        // Keep polling; the hosted runner may outlive transient API/network errors.
      }
    };

    const timer = setInterval(poll, MANAGED_ASTRO_IMPORT_POLL_INTERVAL_MS);
    void poll();
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [api, notifyImportReady, pollImportId]);

  function resetManagedAstroSourceSelection() {
    setAnalysis(null);
    setBuildReport(null);
    setImportRecord(null);
    setManagedAstroLastCheckedAt(null);
    setSourceArtifact(null);
    setSanityExport(null);
    setEnvConfigValues({});
    setError(null);
    setPrepared(null);
  }

  async function onFilesSelected(files: FileList | null) {
    resetManagedAstroSourceSelection();
    if (!files || files.length === 0) return;
    try {
      setPrepared(await prepareManagedAstroUpload(Array.from(files)));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not read that project.",
      );
    }
  }

  async function onDirectoryPickerClick() {
    if (!directoryPicker) {
      resetManagedAstroSourceSelection();
      setError("Use the folder input below to choose an Astro project.");
      return;
    }
    try {
      const directory = await directoryPicker({ mode: "read" });
      resetManagedAstroSourceSelection();
      setPrepared(
        await prepareManagedAstroUploadFromDirectoryHandle(directory),
      );
    } catch (err) {
      if (isAbortError(err)) return;
      setError(
        err instanceof Error
          ? err.message
          : "Could not read that project folder.",
      );
    }
  }

  async function onSanityExportSelected(files: FileList | null) {
    setError(null);
    setSanityExport(null);
    const file = files?.item(0);
    if (!file) return;
    if (!prepared) {
      setError(
        "Choose the Astro project folder before adding a Sanity export.",
      );
      return;
    }
    try {
      setSanityExport(
        await prepareManagedAstroSanityExportUpload(
          file,
          prepared.files.map((entry) => entry.path),
        ),
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not read that Sanity export.",
      );
    }
  }

  async function analyze() {
    if (!prepared || prepared.files.length === 0) return;
    const upload = mergeManagedAstroSanityExport(prepared, sanityExport);
    setWorkingStep("analyzing");
    setError(null);
    setBuildReport(null);
    try {
      const result = await api.analyzeManagedAstroImport({
        files: upload.files,
        sourceLabel: upload.sourceLabel,
      });
      setAnalysis(result.analysis);
      setEnvConfigValues(
        seedManagedAstroEnvConfigValues(
          result.analysis.plan.environment.requiredVars,
          {},
        ),
      );
      setImportRecord(result.import);
      setWorkingStep("uploading");
      const sourceResult = await api.uploadManagedAstroSource(
        result.import.id,
        {
          files: upload.files,
          sourceFiles: upload.sourceFiles,
        },
      );
      setImportRecord(sourceResult.import);
      setSourceArtifact(sourceResult.sourceArtifact);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not analyze or save that Astro project.",
      );
    } finally {
      setWorkingStep(null);
    }
  }

  async function resolveEnvConfig() {
    if (!analysis || !importRecord) return;
    const requiredVars = analysis.plan.environment.requiredVars;
    setWorkingStep("resolving-env");
    setError(null);
    try {
      const variables = requiredVars.reduce<Record<string, string>>(
        (acc, varName) => {
          acc[varName] = envConfigValues[varName] ?? "";
          return acc;
        },
        {},
      );
      const result = await api.resolveManagedAstroEnvConfig(importRecord.id, {
        variables,
      });
      setImportRecord(result.import);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not save the managed Astro env config.",
      );
    } finally {
      setWorkingStep(null);
    }
  }

  async function queueBuild() {
    if (!importRecord || !sourceArtifact) return;
    setWorkingStep("queuing");
    setError(null);
    setBuildReport(null);
    try {
      const result = await api.queueManagedAstroBuild(importRecord.id);
      setImportRecord(result.import);
      setSourceArtifact(result.sourceArtifact);
      setManagedAstroLastCheckedAt(new Date().toISOString());
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not queue the managed Astro build.",
      );
    } finally {
      setWorkingStep(null);
    }
  }

  async function loadBuildReport() {
    if (
      !importRecord?.build?.reportKey ||
      !managedAstroBuildCanHaveRunnerReport(importRecord.build)
    ) {
      return;
    }
    setWorkingStep("loading-report");
    setError(null);
    try {
      const result = await api.getManagedAstroBuildReport(importRecord.id);
      setBuildReport(result.report);
      notifyImportReady(importRecord);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not load the managed build report.",
      );
    } finally {
      setWorkingStep(null);
    }
  }

  async function refreshManagedAstroImport() {
    if (!importRecord) return;
    setWorkingStep("refreshing-import");
    setError(null);
    try {
      const result = await api.getManagedAstroImport(importRecord.id);
      setImportRecord(result.import);
      setManagedAstroLastCheckedAt(new Date().toISOString());
      notifyImportReady(result.import);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not refresh the managed Astro import.",
      );
    } finally {
      setWorkingStep(null);
    }
  }

  async function publishImport() {
    if (!importRecord) return;
    setWorkingStep("publishing");
    setError(null);
    try {
      const result = await api.publishManagedAstroImport(importRecord.id);
      setImportRecord(result.import);
      notifyImportReady(result.import);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not publish the managed Astro build.",
      );
    } finally {
      setWorkingStep(null);
    }
  }

  return (
    <div className="source-import-flow">
      {/* Once an import exists, the uploader folds away so the import's
          progress is the hero; it stays open while an upload is in flight
          and the user can always reopen it to replace the source. */}
      <details
        className="source-details source-uploader"
        onToggle={(event) => {
          setUploaderOpen(event.currentTarget.open);
        }}
        open={uploaderOpen}
      >
        <summary>
          {importRecord
            ? `Source upload${prepared ? ` — ${prepared.totalFiles} files scanned` : ""}`
            : "Choose your Astro project"}
        </summary>
        <div className="source-import-intro">
          <strong>Managed Astro import</strong>
          <p className="muted">
            Select the project folder. Groundbeam scans source, Sanity hints,
            forms, and assets, while ignoring dependencies, build output, git
            data, and env files.
          </p>
        </div>

        {directoryPicker ? (
          <>
            <button
              className="source-upload-target source-upload-target-button"
              data-testid="managed-astro-directory-picker"
              onClick={() => {
                void onDirectoryPickerClick();
              }}
              type="button"
            >
              <span>Choose Astro project folder</span>
              <small>
                Skips dependencies, build output, caches, and ignored paths.
              </small>
            </button>
            <label className="source-upload-target secondary">
              <input
                {...directoryInputProps}
                data-testid="managed-astro-source-input"
                multiple
                onChange={(event) => {
                  void onFilesSelected(event.currentTarget.files);
                }}
                type="file"
              />
              <span>Use legacy folder picker</span>
              <small>Fallback for browsers without direct folder access.</small>
            </label>
          </>
        ) : (
          <label className="source-upload-target">
            <input
              {...directoryInputProps}
              data-testid="managed-astro-source-input"
              multiple
              onChange={(event) => {
                void onFilesSelected(event.currentTarget.files);
              }}
              type="file"
            />
            <span>Choose Astro project folder</span>
            <small>Use the repo root, for example lca-demo.</small>
          </label>
        )}

        {prepared && (
          <div className="source-import-review">
            <div className="source-import-stats">
              <Stat label="Files scanned" value={String(prepared.totalFiles)} />
              <Stat
                label="Source text"
                value={formatBytes(prepared.textBytes)}
              />
              <Stat
                label="Project size"
                value={formatBytes(prepared.totalBytes)}
              />
            </div>
            {prepared.skippedFiles > 0 && (
              <p className="muted small">
                Ignored {prepared.skippedFiles} dependency, build, git, or
                secret item{prepared.skippedFiles === 1 ? "" : "s"}.
              </p>
            )}
            <label className="source-upload-target secondary">
              <input
                accept=".ndjson,.json,application/json,application/x-ndjson"
                onChange={(event) => {
                  void onSanityExportSelected(event.currentTarget.files);
                }}
                type="file"
              />
              <span>Add Sanity export</span>
              <small>
                Optional for private datasets or projects without public query
                access.
              </small>
            </label>
            {sanityExport && (
              <p className="muted small source-upload-note">
                Attached {sanityExport.originalName} as {sanityExport.path} (
                {formatBytes(sanityExport.sizeBytes)}).
              </p>
            )}
            <button
              className="source-analyze-button"
              data-testid="managed-astro-analyze-source"
              disabled={workingStep !== null || prepared.files.length === 0}
              onClick={() => {
                void analyze();
              }}
              type="button"
            >
              {workingStep === "analyzing"
                ? "Analyzing..."
                : workingStep === "uploading"
                  ? "Uploading source..."
                  : "Analyze and save source"}
            </button>
          </div>
        )}
      </details>

      {error && <p className="error">{error}</p>}

      {analysis && importRecord && (
        <ManagedAstroAnalysisReport
          analysis={analysis}
          buildReport={buildReport}
          envConfigValues={envConfigValues}
          importRecord={importRecord}
          isLoadingReport={workingStep === "loading-report"}
          isPublishing={workingStep === "publishing"}
          isQueuing={workingStep === "queuing"}
          isRefreshingImport={workingStep === "refreshing-import"}
          isResolvingEnv={workingStep === "resolving-env"}
          lastCheckedAt={managedAstroLastCheckedAt}
          onEnvConfigChange={(name, value) => {
            setEnvConfigValues((current) => ({
              ...current,
              [name]: value,
            }));
          }}
          onLoadBuildReport={loadBuildReport}
          onPublish={publishImport}
          onQueueBuild={queueBuild}
          onRefreshImport={refreshManagedAstroImport}
          onResolveEnvConfig={resolveEnvConfig}
          sourceArtifact={sourceArtifact}
        />
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <span>
      <strong>{value}</strong>
      <small>{label}</small>
    </span>
  );
}

function ManagedAstroAnalysisReport({
  analysis,
  buildReport,
  envConfigValues,
  importRecord,
  isLoadingReport,
  isPublishing,
  isQueuing,
  isRefreshingImport,
  isResolvingEnv,
  lastCheckedAt,
  onEnvConfigChange,
  onLoadBuildReport,
  onPublish,
  onQueueBuild,
  onRefreshImport,
  onResolveEnvConfig,
  sourceArtifact,
}: {
  analysis: ManagedAstroImportAnalysis;
  buildReport: ManagedAstroRunnerReport | null;
  envConfigValues: Record<string, string>;
  importRecord: ManagedAstroImportRecord;
  isLoadingReport: boolean;
  isPublishing: boolean;
  isQueuing: boolean;
  isRefreshingImport: boolean;
  isResolvingEnv: boolean;
  lastCheckedAt: string | null;
  onEnvConfigChange: (name: string, value: string) => void;
  onLoadBuildReport: () => void;
  onPublish: () => void;
  onQueueBuild: () => void;
  onRefreshImport: () => void;
  onResolveEnvConfig: () => void;
  sourceArtifact: ManagedAstroSourceArtifact | null;
}) {
  const required = analysis.plan.operations.filter(
    (operation) => operation.required,
  );
  const openConfig = importRecord.pendingConfig.filter(
    (question) => question.status === "open",
  );
  const hasPreviewBlockingConfig = openConfig.some(
    (question) => question.requiredForPreview,
  );
  const hasPublishBlockingConfig = openConfig.some(
    (question) => question.requiredForPublish,
  );
  const missingEnvOpen = openConfig.some(
    (question) => question.kind === "missing_env",
  );
  const requiredEnvVars = analysis.plan.environment.requiredVars;
  const build = importRecord.build;
  const isPollingManagedBuild = shouldPollManagedAstroImport(importRecord);
  const canLoadRunnerReport =
    build !== null && managedAstroBuildCanHaveRunnerReport(build);
  const canQueueBuild =
    sourceArtifact !== null &&
    !hasPreviewBlockingConfig &&
    !isQueuing &&
    ![
      "building",
      "preview_ready",
      "publish_ready",
      "published",
      "queued",
    ].includes(importRecord.status);
  const hasPassingBuild =
    build?.ok === true &&
    build.status === "passed" &&
    Boolean(build.artifactPrefix);
  const canPublish =
    hasPassingBuild &&
    !hasPublishBlockingConfig &&
    !isPublishing &&
    importRecord.status !== "published" &&
    Boolean(importRecord.publishTarget.tenantHostname);
  const liveTenantUrl = importRecord.publishTarget.tenantHostname
    ? `https://${importRecord.publishTarget.tenantHostname}/`
    : null;
  return (
    <div className="source-analysis-report">
      <div className="source-analysis-head">
        <div>
          <strong>{analysis.plan.packageName ?? "Astro project"}</strong>
          <p className="muted small">
            Import {importRecord.id} - {analysis.plan.packageManager} project
            {analysis.plan.sanity.detected
              ? ` with Sanity ${analysis.plan.sanity.projectId ?? "detected"}`
              : " without detected Sanity"}
          </p>
        </div>
        <span>{managedAstroStatusLabel(importRecord.status)}</span>
      </div>

      <ManagedAstroBuildProgress
        build={build}
        importRecord={importRecord}
        lastCheckedAt={lastCheckedAt}
        sourceArtifactSaved={sourceArtifact !== null}
      />

      {openConfig.length > 0 && (
        <div className="source-analysis-section warning">
          <strong>Pending setup</strong>
          <ul>
            {openConfig.map((question) => (
              <li key={question.id}>
                <span>
                  {question.title} -{" "}
                  {managedAstroPendingConfigRequirementLabel(question)}
                </span>
                <p className="muted small">{question.description}</p>
              </li>
            ))}
          </ul>
          {missingEnvOpen && requiredEnvVars.length > 0 && (
            <div className="source-env-config">
              <div className="source-env-grid">
                {requiredEnvVars.map((varName) => (
                  <label key={varName}>
                    <span>{varName}</span>
                    <input
                      autoComplete="off"
                      data-env-var={varName}
                      data-testid="managed-astro-env-input"
                      onChange={(event) => {
                        onEnvConfigChange(varName, event.currentTarget.value);
                      }}
                      placeholder="Value for build runner"
                      type={varName.startsWith("PUBLIC_") ? "text" : "password"}
                      value={envConfigValues[varName] ?? ""}
                    />
                  </label>
                ))}
              </div>
              <button
                className="source-analyze-button secondary"
                data-testid="managed-astro-save-env-config"
                disabled={
                  isResolvingEnv ||
                  !managedAstroEnvConfigCanSubmit(
                    requiredEnvVars,
                    envConfigValues,
                  )
                }
                onClick={() => {
                  onResolveEnvConfig();
                }}
                type="button"
              >
                {isResolvingEnv ? "Saving env vars..." : "Save env vars"}
              </button>
            </div>
          )}
        </div>
      )}

      <details className="source-details">
        <summary>
          Project details
          {analysis.warnings.length > 0
            ? ` — ${analysis.warnings.length} warning${
                analysis.warnings.length === 1 ? "" : "s"
              }`
            : ""}
        </summary>
        <div className="source-import-stats">
          <Stat
            label="Assets"
            value={`${analysis.summary.assetFiles} / ${formatBytes(
              analysis.summary.assetBytes,
            )}`}
          />
          <Stat label="Forms" value={String(analysis.summary.formFileCount)} />
          <Stat
            label="Env vars"
            value={managedAstroEnvVarSummary(analysis.plan.environment)}
          />
          <Stat
            label="Verifier gates"
            value={String(analysis.summary.verificationGateCount)}
          />
        </div>
        <div className="source-analysis-section">
          <strong>Required work</strong>
          <ul>
            {required.slice(0, 6).map((operation) => (
              <li key={operation.type}>{operation.reason}</li>
            ))}
          </ul>
        </div>
        {analysis.warnings.length > 0 && (
          <div className="source-analysis-section warning">
            <strong>Needs attention</strong>
            <ul>
              {analysis.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </div>
        )}
      </details>

      {sourceArtifact && (
        <div className="source-analysis-section source-artifact-saved">
          <div>
            <strong>Source bundle saved</strong>
            <p className="muted small">
              {sourceArtifact.fileCount} files,{" "}
              {formatBytes(sourceArtifact.totalBytes)} stored for the managed
              build runner.
            </p>
          </div>
          <button
            className="source-analyze-button"
            data-testid="managed-astro-queue-build"
            disabled={!canQueueBuild}
            onClick={() => {
              onQueueBuild();
            }}
            type="button"
          >
            {managedAstroBuildActionLabel({ build, isQueuing })}
          </button>
        </div>
      )}

      {build && (
        <div
          className={`source-analysis-section source-build-status ${build.status}`}
        >
          <div className="source-build-status-head">
            <div>
              <strong>Managed build</strong>
              <p className="muted small">
                {managedAstroBuildStatusLabel(build.status)}
                {build.routes.length > 0
                  ? ` - ${build.routes.length} route${
                      build.routes.length === 1 ? "" : "s"
                    }`
                  : ""}
              </p>
            </div>
            <div className="source-build-status-actions">
              {isPollingManagedBuild && (
                <button
                  className="source-analyze-button secondary"
                  data-testid="managed-astro-refresh-status"
                  disabled={isRefreshingImport}
                  onClick={() => {
                    onRefreshImport();
                  }}
                  type="button"
                >
                  {isRefreshingImport ? "Refreshing..." : "Refresh status"}
                </button>
              )}
              {build.previewUrl && (
                <a
                  className="source-build-preview-link"
                  data-testid="managed-astro-preview-link"
                  href={build.previewUrl}
                  rel="noreferrer"
                  target="_blank"
                  title="Preview exactly this build's artifacts (the editor's Open preview shows the latest preview-ready build on your preview site)."
                >
                  Preview this build
                </a>
              )}
            </div>
          </div>
          {build.error && <p className="error small">{build.error}</p>}
          {build.reportKey && canLoadRunnerReport && (
            <details className="source-details">
              <summary>Build report</summary>
              <div className="source-build-report-control">
                <p className="muted small">
                  Runner report: <code>{build.reportKey}</code>
                </p>
                <button
                  className="source-analyze-button secondary"
                  data-testid="managed-astro-load-report"
                  disabled={isLoadingReport}
                  onClick={() => {
                    onLoadBuildReport();
                  }}
                  type="button"
                >
                  {isLoadingReport
                    ? "Loading report..."
                    : buildReport
                      ? "Refresh report"
                      : "Load runner report"}
                </button>
              </div>
              {buildReport && (
                <ManagedAstroBuildReportSummary report={buildReport} />
              )}
            </details>
          )}
        </div>
      )}

      <div className="source-analysis-section source-publish-target">
        <details className="source-details">
          <summary>Publish target &amp; domains</summary>
          <dl>
            <div className="source-publish-row">
              <dt>Tenant host</dt>
              <dd>{importRecord.publishTarget.tenantHostname ?? "Pending"}</dd>
            </div>
            <div className="source-publish-row">
              <dt>CNAME target</dt>
              <dd>{importRecord.publishTarget.cnameTarget ?? "Pending"}</dd>
            </div>
            <div className="source-publish-row">
              <dt>Custom domain</dt>
              <dd>
                {importRecord.publishTarget.customDomain ?? "Not configured"}
              </dd>
            </div>
            {importRecord.publishTarget.customDomain && (
              <div className="source-publish-row">
                <dt>DNS status</dt>
                <dd>
                  {managedAstroDnsStatusLabel(
                    importRecord.publishTarget.dnsStatus,
                  )}
                </dd>
              </div>
            )}
          </dl>
        </details>
        <div className="source-publish-actions">
          {importRecord.status === "published" && liveTenantUrl ? (
            <a
              className="source-publish-link"
              href={liveTenantUrl}
              rel="noreferrer"
              target="_blank"
            >
              Open live tenant site
            </a>
          ) : (
            <button
              className="source-publish-button"
              data-testid="managed-astro-publish"
              disabled={!canPublish}
              onClick={() => {
                onPublish();
              }}
              type="button"
            >
              {isPublishing ? "Publishing..." : "Publish to tenant host"}
            </button>
          )}
          {hasPublishBlockingConfig && (
            <p className="muted small">
              Resolve publish-blocking setup before replacing the tenant site.
            </p>
          )}
        </div>
      </div>

      <div className="source-analysis-section">
        <strong>Next step</strong>
        <p className="muted small">
          {managedAstroNextStepCopy({
            build,
            hasPublishBlockingConfig,
            hasPreviewBlockingConfig,
            hasSourceArtifact: sourceArtifact !== null,
            status: importRecord.status,
          })}
        </p>
      </div>
    </div>
  );
}

export interface ManagedAstroImportStep {
  key?: string;
  label: string;
  state: "complete" | "active" | "pending" | "failed";
}

export function shouldPollGenerateImport(status: string): boolean {
  return ["analysis_ready", "queued", "building"].includes(status);
}

export function generateImportSteps({
  status,
  buildCommand,
}: {
  status: string;
  /** record.build?.command — "generate-site" marks a pre-build generation failure. */
  buildCommand: string | null;
}): ManagedAstroImportStep[] {
  const published = status === "published";
  const previewReady = ["preview_ready", "publish_ready", "published"].includes(
    status,
  );
  const buildActive = status === "queued" || status === "building";
  const failed = status === "failed";
  // A rejected generation is marked failed with command "generate-site"
  // BEFORE any runner build exists; runner failures carry the runner command.
  const generationFailed =
    failed && (buildCommand === "generate-site" || buildCommand === null);
  const buildFailed = failed && !generationFailed;
  return [
    {
      key: "writing",
      label: "Writing your site",
      state: generationFailed
        ? "failed"
        : status === "analysis_ready"
          ? "active"
          : "complete",
    },
    {
      key: "build",
      label: "Build",
      state: buildFailed
        ? "failed"
        : previewReady
          ? "complete"
          : buildActive
            ? "active"
            : "pending",
    },
    {
      key: "quality",
      label: "Quality checks",
      state: buildFailed ? "failed" : previewReady ? "complete" : "pending",
    },
    {
      key: "preview",
      label: "Preview",
      state: previewReady ? "complete" : "pending",
    },
    {
      key: "published",
      label: "Published",
      state: published ? "complete" : previewReady ? "active" : "pending",
    },
  ];
}

/**
 * The calm-by-default stage model (founder feedback 2026-07-07): normal
 * users care about stage names + a moving indicator, nothing else. One
 * ordered list from upload to published; everything verbose lives under
 * collapsibles elsewhere.
 */
/**
 * §15.3 quality-check results for a completed URL migration: one status
 * line plus per-section sentences. Renders nothing for pre-fidelity jobs.
 */
function MirrorFidelitySummary({
  check,
}: {
  check: MirrorFidelityCheck | null;
}) {
  if (!check) return null;
  const statusLine = mirrorFidelityStatusLine(check);
  if (!statusLine) return null;
  const lines =
    check.status === "complete" && check.report
      ? mirrorFidelitySectionLines(check.report)
      : [];
  return (
    <div className="fidelity-summary" data-testid="mirror-fidelity-summary">
      <p
        className={
          check.report?.status === "failed" ? "error small" : "muted small"
        }
      >
        {statusLine}
      </p>
      {lines.length > 0 && (
        <details className="fidelity-details">
          <summary>Quality check details</summary>
          <ul className="fidelity-lines">
            {lines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

export function managedAstroImportSteps(input: {
  buildOk: boolean | null;
  buildStatus: "queued" | "running" | "passed" | "failed" | null;
  hasPreview: boolean;
  hasSourceArtifact: boolean;
  status: ManagedAstroImportStatus;
}): ManagedAstroImportStep[] {
  const published = input.status === "published";
  const buildDone = input.buildStatus === "passed" && input.buildOk === true;
  const buildFailed =
    input.buildStatus === "failed" ||
    (input.buildStatus === "passed" && input.buildOk === false) ||
    input.status === "failed";
  const building =
    input.buildStatus === "queued" || input.buildStatus === "running";

  return [
    {
      label: "Source saved",
      state: input.hasSourceArtifact ? "complete" : "active",
    },
    {
      label:
        input.buildStatus === "queued"
          ? "Waiting for builder"
          : input.buildStatus === "running"
            ? "Building"
            : "Build",
      state: buildDone
        ? "complete"
        : buildFailed
          ? "failed"
          : building
            ? "active"
            : "pending",
    },
    {
      label: "Quality checks",
      state: buildDone ? "complete" : buildFailed ? "failed" : "pending",
    },
    {
      label: "Preview",
      state: input.hasPreview ? "complete" : buildDone ? "active" : "pending",
    },
    {
      label: "Published",
      state: published ? "complete" : input.hasPreview ? "active" : "pending",
    },
  ];
}

function ManagedAstroBuildProgress({
  build,
  importRecord,
  lastCheckedAt,
  sourceArtifactSaved,
}: {
  build: ManagedAstroImportRecord["build"];
  importRecord: ManagedAstroImportRecord;
  lastCheckedAt: string | null;
  sourceArtifactSaved: boolean;
}) {
  const steps = managedAstroImportSteps({
    buildOk: build?.ok ?? null,
    buildStatus: build?.status ?? null,
    hasPreview: Boolean(build?.previewUrl),
    hasSourceArtifact: sourceArtifactSaved,
    status: importRecord.status,
  });
  const running = build?.status === "queued" || build?.status === "running";
  return (
    <div
      className="source-runner-progress"
      data-testid="managed-astro-progress"
    >
      <div className="source-runner-progress-head">
        {/* the slot ALWAYS renders — the head is a [12px 1fr] grid, and an
            absent pulse used to drop the text into the 12px column */}
        <span
          aria-hidden="true"
          className={
            running ? "source-runner-pulse" : "source-runner-pulse idle"
          }
        />
        <div>
          <strong>
            {build
              ? managedAstroRunnerProgressTitle(build.status)
              : "Ready to build"}
          </strong>
          <p className="muted small">
            {build
              ? managedAstroRunnerProgressCopy(build.status)
              : "Save the source bundle, then queue the managed build."}
          </p>
        </div>
      </div>
      <ol className="source-runner-steps" aria-label="Managed build progress">
        {steps.map((step) => (
          <li className={step.state} key={step.label}>
            <span />
            {step.label}
          </li>
        ))}
      </ol>
      {running && (
        <div className="source-runner-meta">
          <span>{MANAGED_ASTRO_RUNNER_SCHEDULE_COPY}</span>
          <span>
            {lastCheckedAt
              ? `Last status check: ${formatDateTime(lastCheckedAt)}`
              : "First status check is starting now."}
          </span>
          <span>Import {importRecord.id}</span>
        </div>
      )}
    </div>
  );
}

function ManagedAstroBuildReportSummary({
  report,
}: {
  report: ManagedAstroRunnerReport;
}) {
  const stats = managedAstroBuildReportStats(report);
  return (
    <div className="source-build-report-summary">
      <div className="source-import-stats">
        <Stat
          label="Verifier"
          value={`${stats.passedGates}/${stats.totalGates}`}
        />
        <Stat
          label="Assets copied"
          value={`${stats.copiedAssets}/${stats.totalAssets}`}
        />
        <Stat label="Sanity" value={stats.sanityLabel} />
        <Stat label="Editable" value={String(stats.editableEntries)} />
      </div>
      <p className="muted small">
        Visual QA {stats.visualQaLabel} - {stats.patchCount} source patch
        {stats.patchCount === 1 ? "" : "es"} - generated{" "}
        {formatDateTime(report.generatedAt)}
      </p>
      {stats.failedGates > 0 && (
        <ul className="source-build-report-failures">
          {report.verifier.gates
            .filter((gate) => gate.status === "failed")
            .slice(0, 3)
            .map((gate) => (
              <li key={gate.gate}>{gate.detail}</li>
            ))}
        </ul>
      )}
    </div>
  );
}

export function managedAstroBuildReportStats(report: ManagedAstroRunnerReport) {
  const totalGates = report.verifier.gates.length;
  const passedGates = report.verifier.gates.filter(
    (gate) => gate.status === "passed",
  ).length;
  const failedGates = report.verifier.gates.filter(
    (gate) => gate.status === "failed",
  ).length;
  const totalAssets =
    report.assetCopies.length || report.mediaManifest.assets.length;
  const copiedAssets = report.assetCopies.filter(
    (asset) => asset.status === "copied",
  ).length;
  const sanityLabel =
    report.sanity.status === "not_detected"
      ? "None"
      : `${report.sanity.documentsTotal} ${report.sanity.status.replaceAll(
          "_",
          " ",
        )}`;
  const visualQaLabel =
    report.visualQa.status === "not_run"
      ? "not run"
      : `${report.visualQa.status} (${report.visualQa.artifacts.length} artifact${
          report.visualQa.artifacts.length === 1 ? "" : "s"
        })`;
  return {
    copiedAssets,
    editableEntries: report.editableManifest.entries.length,
    failedGates,
    patchCount: report.patches.length,
    passedGates,
    sanityLabel,
    totalAssets,
    totalGates,
    visualQaLabel,
  };
}

export function managedAstroPendingConfigRequirementLabel(
  question: Pick<
    ManagedAstroPendingConfigQuestion,
    "requiredForPreview" | "requiredForPublish"
  >,
): string {
  if (question.requiredForPreview) return "required before preview";
  if (question.requiredForPublish) return "required before publish";
  return "setup reminder";
}

export function managedAstroDnsStatusLabel(
  status: ManagedAstroImportRecord["publishTarget"]["dnsStatus"],
): string {
  const labels: Record<
    ManagedAstroImportRecord["publishTarget"]["dnsStatus"],
    string
  > = {
    not_configured: "Not configured",
    pending: "Pending CNAME verification",
    ready: "Ready",
  };
  return labels[status];
}

export function managedAstroEnvVarSummary(input: {
  referencedVars: string[];
  requiredVars: string[];
}): string {
  if (input.requiredVars.length > 0) {
    return `${input.requiredVars.length} required`;
  }
  if (input.referencedVars.length > 0) {
    return `${input.referencedVars.length} referenced`;
  }
  return "None";
}

export function seedManagedAstroEnvConfigValues(
  requiredVars: readonly string[],
  current: Record<string, string>,
): Record<string, string> {
  return requiredVars.reduce<Record<string, string>>((acc, varName) => {
    acc[varName] = current[varName] ?? "";
    return acc;
  }, {});
}

export function managedAstroEnvConfigCanSubmit(
  requiredVars: readonly string[],
  values: Record<string, string>,
): boolean {
  return (
    requiredVars.length > 0 &&
    requiredVars.every((varName) => (values[varName] ?? "").trim().length > 0)
  );
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function managedAstroNextStepCopy({
  build,
  hasPublishBlockingConfig,
  hasPreviewBlockingConfig,
  hasSourceArtifact,
  status,
}: {
  build: ManagedAstroImportRecord["build"];
  hasPublishBlockingConfig: boolean;
  hasPreviewBlockingConfig: boolean;
  hasSourceArtifact: boolean;
  status: ManagedAstroImportStatus;
}) {
  if (status === "published") {
    return "The managed Astro build is live on the tenant hostname. Keep future changes behind preview and verifier checks before publishing again.";
  }
  if (hasPreviewBlockingConfig) {
    return "Resolve preview-blocking setup before the managed build runner can start.";
  }
  if (!hasSourceArtifact) {
    return "This durable import is ready for source upload. Publish stays blocked until build, form, asset, script, and visual checks pass.";
  }
  if (!build) {
    return "The source bundle is stored. Queue the managed build to create a previewable tenant site.";
  }
  if (build.status === "queued" || build.status === "running") {
    return "The managed build runner is working. Preview and publish stay blocked until the runner reports a passing build.";
  }
  if (build.status === "failed") {
    return "Review the runner error, adjust the import or pending setup, then retry the managed build.";
  }
  if (hasPublishBlockingConfig) {
    return "Preview is ready. Resolve publish-blocking setup before replacing the tenant site.";
  }
  if (build.previewUrl) {
    return "Preview is ready. Publish to the tenant hostname once the review is approved.";
  }
  return "The build passed. Generate a preview URL before publishing this tenant site.";
}

function managedAstroBuildStatusLabel(
  status: NonNullable<ManagedAstroImportRecord["build"]>["status"],
): string {
  const labels: Record<
    NonNullable<ManagedAstroImportRecord["build"]>["status"],
    string
  > = {
    failed: "Failed",
    passed: "Passed",
    queued: "Queued",
    running: "Running",
  };
  return labels[status];
}

export function managedAstroBuildCanHaveRunnerReport(
  build: ManagedAstroImportRecord["build"],
): boolean {
  return build?.status === "passed" || build?.status === "failed";
}

export function managedAstroRunnerProgressTitle(
  status: NonNullable<ManagedAstroImportRecord["build"]>["status"],
): string {
  if (status === "running") return "Runner is building the preview";
  if (status === "queued") return "Waiting for the managed runner";
  if (status === "failed") return "Runner finished with errors";
  return "Runner finished";
}

export function managedAstroRunnerProgressCopy(
  status: NonNullable<ManagedAstroImportRecord["build"]>["status"],
): string {
  if (status === "running") {
    return "The runner has picked up this import and is installing, patching, building, copying assets, and running verifier gates.";
  }
  if (status === "queued") {
    return "The source bundle is saved and waiting for the hosted runner. You can leave this screen open; status refreshes automatically.";
  }
  if (status === "failed") {
    return "The runner produced a report with the failure details. Review it, adjust setup, then retry the build.";
  }
  return "The runner produced a passing report and the preview can be reviewed before publishing.";
}

function managedAstroBuildActionLabel({
  build,
  isQueuing,
}: {
  build: ManagedAstroImportRecord["build"];
  isQueuing: boolean;
}) {
  if (isQueuing) return "Queueing...";
  if (!build) return "Queue managed build";
  if (build.status === "failed") return "Retry managed build";
  if (build.status === "running") return "Build running";
  if (build.status === "passed") return "Build passed";
  return "Build queued";
}

function managedAstroStatusLabel(status: ManagedAstroImportStatus): string {
  const labels: Record<ManagedAstroImportStatus, string> = {
    analysis_ready: "Analysis ready",
    building: "Building",
    config_pending: "Setup needed",
    failed: "Failed",
    preview_ready: "Preview ready",
    publish_ready: "Ready to publish",
    published: "Published",
    queued: "Queued",
  };
  return labels[status];
}

/**
 * §15.3 preview-first phases: the migration snapshot is on the preview host
 * awaiting its fidelity verdict (or an operator decision) — the live site has
 * not changed yet. Jobs whose phase is `complete` predate the gate.
 */
export function isUrlMigrationGatePhase(phase: string | null): boolean {
  return (
    phase === "quality_checks" ||
    phase === "publish_blocked" ||
    phase === "checks_incomplete"
  );
}

export function shouldNotifyImportComplete(
  job: MigrationJob,
  notifiedJobIds: ReadonlySet<string>,
) {
  if (job.status !== "complete" || notifiedJobIds.has(job.id)) return false;
  // Preview-first jobs aren't live yet — the notification (collapse +
  // parent refresh) fires when the phase reaches `published`, not at capture.
  return !isUrlMigrationGatePhase(job.phase);
}

export interface UrlMigrationStep {
  label: string;
  state: "pending" | "active" | "complete" | "failed";
}

/**
 * Stage-first progress for the URL/mirror lane (same calm shape as the
 * managed-Astro stepper): stage names + one active stage, no machinery.
 * Preview-first (§15.3): the mirror lands on the preview host, quality
 * checks measure it there, and the verdict drives the final "Live" step —
 * auto-publish on pass, an operator decision (with note) on fail. Jobs
 * whose phase is `complete` predate the gate and were live at capture time.
 */
export function urlMigrationSteps(input: {
  fidelityReportStatus: string | null;
  fidelityStatus: MirrorFidelityCheck["status"] | null;
  jobStatus: MigrationJob["status"] | null;
  phase: string | null;
}): UrlMigrationStep[] {
  const jobFailed = input.jobStatus === "failed";
  const jobComplete = input.jobStatus === "complete";
  const phase = input.phase ?? "";

  // Which of the three migration stages is running, by phase name.
  const stageIndexByPhase: Record<string, number> = {
    starting: 0,
    capturing: 0,
    rendering: 0,
    publishing: 1,
    enriching: 2,
  };
  const activeIndex = jobComplete ? 3 : (stageIndexByPhase[phase] ?? 0);

  const stageState = (index: number): UrlMigrationStep["state"] => {
    if (index < activeIndex) return "complete";
    if (index > activeIndex) return "pending";
    return jobFailed ? "failed" : "active";
  };

  let checksState: UrlMigrationStep["state"] = "pending";
  let liveState: UrlMigrationStep["state"] = "pending";
  if (jobComplete) {
    if (phase === "quality_checks") {
      checksState = input.fidelityStatus === "failed" ? "failed" : "active";
    } else if (phase === "publish_blocked" || phase === "checks_incomplete") {
      checksState = "failed";
    } else if (phase === "published") {
      checksState =
        input.fidelityStatus === "failed" ||
        input.fidelityReportStatus === "failed"
          ? "failed"
          : "complete";
      liveState = "complete";
    } else {
      // Legacy pre-gate job: live at capture time, checks ran post-publish.
      liveState = "complete";
      if (input.fidelityStatus === "failed") checksState = "failed";
      else if (input.fidelityStatus === "complete") {
        checksState =
          input.fidelityReportStatus === "failed" ? "failed" : "complete";
      } else checksState = "active";
    }
  }

  return [
    { label: "Reading your site", state: stageState(0) },
    { label: "Preparing pages", state: stageState(1) },
    { label: "Importing content", state: stageState(2) },
    { label: "Quality checks", state: checksState },
    { label: "Live", state: liveState },
  ];
}

/**
 * Keep polling a completed migration while its §15.3 quality checks are
 * still pending. A gate-phased job polls through `quality_checks` (the
 * verdict flips it to published/blocked/incomplete server-side); the
 * terminal gate phases stop — the publish CTA takes over. Bounded when the
 * fidelity row hasn't appeared (older jobs predate the fidelity queue and
 * would otherwise poll forever).
 */
export function shouldContinueImportPolling({
  fidelityStatus,
  jobStatus,
  phase,
  pollsSinceComplete,
}: {
  fidelityStatus: MirrorFidelityCheck["status"] | null;
  jobStatus: MigrationJob["status"];
  phase: string | null;
  pollsSinceComplete: number;
}): boolean {
  if (jobStatus === "queued" || jobStatus === "running") return true;
  if (jobStatus === "failed") return false;
  if (phase === "quality_checks") {
    // The verdict decides whether this site goes live, and the runner cron
    // fallback fires every 15 minutes — keep watching a little past that
    // (polls run every 1.6s). A reload re-adopts the job if this ever lapses.
    return pollsSinceComplete < 650;
  }
  if (
    phase === "publish_blocked" ||
    phase === "checks_incomplete" ||
    phase === "published"
  ) {
    return false;
  }
  // Legacy pre-gate jobs: watch the fidelity row itself.
  if (fidelityStatus === "queued" || fidelityStatus === "running") {
    return pollsSinceComplete < 200;
  }
  if (fidelityStatus === null) return pollsSinceComplete < 6;
  return false; // complete/failed fidelity — nothing left to watch
}

/** One calm line for the compact "Website imported" card. */
export function mirrorFidelityStatusLine(
  check: Pick<MirrorFidelityCheck, "status" | "report"> | null,
): string | null {
  if (!check) return null;
  if (check.status === "queued" || check.status === "running") {
    return "Quality checks running...";
  }
  if (check.status === "failed") {
    return "Quality checks could not run.";
  }
  const report = check.report;
  if (!report || report.status === "not_run") return null;
  if (report.status === "passed") return "Quality checks passed.";
  if (report.status === "advisory") {
    return "Quality checks passed with notes.";
  }
  return "Quality checks found problems.";
}

/**
 * Per-section quality-check lines for the expanded import view. Plain
 * customer-readable sentences — section internals stay in the report JSON.
 */
export function mirrorFidelitySectionLines(
  report: NonNullable<MirrorFidelityCheck["report"]>,
): string[] {
  const lines: string[] = [];

  const behavioral = report.behavioral;
  if (behavioral && behavioral.status !== "skipped") {
    if (behavioral.leadLanded && behavioral.attributionCaptured) {
      lines.push("Lead capture: working, with ad attribution recorded.");
    } else if (behavioral.leadLanded) {
      lines.push("Lead capture: working, but attribution was not recorded.");
    } else {
      lines.push("Lead capture: a test lead did not arrive.");
    }
  }

  const visual = report.visual;
  if (visual && visual.status !== "skipped") {
    // Only homepage diffs feed the verdict (§15.3 1d): subroute entries
    // (route other than "/") are diagnosis and stay out of the headline.
    const gateDiffs = visual.viewports.filter(
      (viewport) => viewport.route == null || viewport.route === "/",
    );
    const scores = gateDiffs
      .map((viewport) =>
        viewport.similarity === null
          ? null
          : `${viewport.viewport} ${(viewport.similarity * 100).toFixed(1)}%`,
      )
      .filter((value): value is string => value !== null);
    lines.push(
      scores.length > 0
        ? `Visual match vs your old site: ${scores.join(", ")}.`
        : "Visual match: could not be measured.",
    );
    const subroutePages = new Set(
      visual.viewports
        .map((viewport) => viewport.route)
        .filter((route): route is string => route != null && route !== "/"),
    );
    if (subroutePages.size > 0) {
      lines.push(
        `Also compared ${subroutePages.size} more page${subroutePages.size === 1 ? "" : "s"} for diagnosis.`,
      );
    }
  }

  const performance = report.performance;
  if (performance && performance.status !== "skipped") {
    const score = performance.lighthouse?.performance;
    const lcp =
      performance.lcpMs !== null
        ? ` (loads in ${(performance.lcpMs / 1000).toFixed(1)}s)`
        : "";
    lines.push(
      score !== null && score !== undefined
        ? `Speed score: ${Math.round(score)}/100${lcp}.`
        : "Speed: could not be measured.",
    );
  }

  const links = report.links;
  if (links && links.status !== "skipped") {
    lines.push(
      links.broken.length === 0
        ? `Links: all ${links.checked} checked links work.`
        : `Links: ${links.broken.length} of ${links.checked} checked links are broken.`,
    );
  }

  return lines;
}

/** WordPress-door visibility: what the enrichment imported, in one line. */
export function wordPressEnrichmentLine(
  enrichment: Pick<
    WordPressRestCmsEnrichment,
    "detected" | "entries" | "collections"
  > | null,
): string | null {
  if (!enrichment?.detected) return null;
  if (enrichment.entries === 0) return "WordPress detected; no content found.";
  return `WordPress content imported: ${enrichment.entries} ${
    enrichment.entries === 1 ? "entry" : "entries"
  } across ${enrichment.collections} ${
    enrichment.collections === 1 ? "collection" : "collections"
  }.`;
}

export function shouldNotifyManagedAstroImportReady(
  importRecord: Pick<ManagedAstroImportRecord, "id" | "status"> | null,
  notifiedImportIds: ReadonlySet<string>,
) {
  if (!importRecord || notifiedImportIds.has(importRecord.id)) return false;
  return (
    importRecord.status === "preview_ready" ||
    importRecord.status === "publish_ready" ||
    importRecord.status === "published"
  );
}

export function shouldPollManagedAstroImport(
  importRecord: Pick<ManagedAstroImportRecord, "status"> | null,
) {
  return (
    importRecord?.status === "queued" || importRecord?.status === "building"
  );
}

export function migratePanelStartsExpanded(hostname: string | null) {
  return !hostname;
}

export function shouldShowCompactMigratePanel({
  hasError,
  hostname,
  isExpanded,
  isRunning,
  jobStatus,
}: {
  hasError: boolean;
  hostname: string | null;
  isExpanded: boolean;
  isRunning: boolean;
  jobStatus: MigrationJob["status"] | null;
}) {
  return (
    Boolean(hostname) &&
    !isExpanded &&
    !isRunning &&
    !hasError &&
    jobStatus !== "failed"
  );
}

export async function prepareManagedAstroUpload(
  selectedFiles: readonly File[],
): Promise<PreparedManagedAstroUpload> {
  const rawFiles = selectedFiles.map((file) => ({
    file,
    path: normalizeUploadPath(
      (file as File & { webkitRelativePath?: string }).webkitRelativePath ||
        file.name,
    ),
  }));
  const sourceLabel = commonTopLevelFolder(rawFiles.map((entry) => entry.path));
  return prepareManagedAstroUploadFromRawFiles(rawFiles, sourceLabel);
}

export async function prepareManagedAstroUploadFromDirectoryHandle(
  directory: ManagedAstroDirectoryHandle,
): Promise<PreparedManagedAstroUpload> {
  const collected = await collectManagedAstroDirectoryFiles(directory);
  return prepareManagedAstroUploadFromRawFiles(
    collected.files,
    normalizeUploadPath(directory.name) || null,
    collected.skippedFiles,
  );
}

async function prepareManagedAstroUploadFromRawFiles(
  rawFiles: readonly RawManagedAstroUploadFile[],
  sourceLabel: string | null,
  preSkippedFiles = 0,
): Promise<PreparedManagedAstroUpload> {
  const gitignoreRules = await loadGitignoreRules(rawFiles, sourceLabel);
  let textBudget = MAX_TOTAL_TEXT_BYTES;
  let skippedFiles = preSkippedFiles;
  const uploadFiles: ManagedAstroUploadFile[] = [];
  const sourceFiles: ManagedAstroSourceUploadFile[] = [];

  for (const entry of rawFiles) {
    const path = stripTopLevelFolder(entry.path, sourceLabel);
    if (!path || shouldSkipSourcePath(path, gitignoreRules)) {
      skippedFiles += 1;
      continue;
    }
    let text: string | null = null;
    if (shouldReadText(path) && entry.file.size <= MAX_TEXT_FILE_BYTES) {
      if (entry.file.size <= textBudget) {
        text = await entry.file.text();
        textBudget -= entry.file.size;
      }
    }
    uploadFiles.push({
      path,
      sizeBytes: entry.file.size,
      mimeType: entry.file.type || null,
      text,
    });
    sourceFiles.push({ file: entry.file, path });
    if (uploadFiles.length > MAX_MANAGED_ASTRO_UPLOAD_FILES) {
      throw new Error(
        `That project still has more than ${MAX_MANAGED_ASTRO_UPLOAD_FILES.toLocaleString()} importable files after ignoring dependencies and build output. Groundbeam currently imports up to ${MAX_MANAGED_ASTRO_UPLOAD_FILES.toLocaleString()} files at once; remove generated folders or import from Git so ignore rules can be applied cleanly.`,
      );
    }
  }

  return {
    files: uploadFiles,
    skippedFiles,
    sourceFiles,
    sourceLabel,
    textBytes: uploadFiles.reduce(
      (sum, file) => sum + (file.text !== null ? file.sizeBytes : 0),
      0,
    ),
    totalBytes: uploadFiles.reduce((sum, file) => sum + file.sizeBytes, 0),
    totalFiles: uploadFiles.length,
  };
}

async function collectManagedAstroDirectoryFiles(
  directory: ManagedAstroDirectoryHandle,
): Promise<{ files: RawManagedAstroUploadFile[]; skippedFiles: number }> {
  const rootName = normalizeUploadPath(directory.name) || "project";
  const files: RawManagedAstroUploadFile[] = [];
  let skippedFiles = 0;

  async function walk(
    currentDirectory: ManagedAstroDirectoryHandle,
    relativeDirectory: string,
    inheritedRules: readonly GitignoreRule[],
  ) {
    const entries: Array<[string, ManagedAstroFileSystemHandle]> = [];
    for await (const entry of currentDirectory.entries()) {
      entries.push(entry);
    }
    const rules = [...inheritedRules];
    const gitignoreEntry = entries.find(
      ([name, handle]) => handle.kind === "file" && name === ".gitignore",
    );
    if (gitignoreEntry?.[1].kind === "file") {
      const file = await gitignoreEntry[1].getFile();
      if (file.size <= MAX_GITIGNORE_BYTES) {
        rules.push(
          ...parseGitignoreRules(await file.text(), relativeDirectory),
        );
      }
    }

    for (const [name, handle] of entries) {
      const path = normalizeUploadPath(
        relativeDirectory ? `${relativeDirectory}/${name}` : name,
      );
      if (!path) continue;
      if (handle.kind === "directory") {
        if (shouldSkipSourceDirectoryPath(path, rules)) {
          skippedFiles += 1;
          continue;
        }
        await walk(handle, path, rules);
        continue;
      }
      if (shouldSkipSourcePath(path, rules)) {
        skippedFiles += 1;
        continue;
      }
      files.push({
        file: await handle.getFile(),
        path: `${rootName}/${path}`,
      });
      if (files.length > MAX_MANAGED_ASTRO_UPLOAD_FILES) {
        throw new Error(
          `That project still has more than ${MAX_MANAGED_ASTRO_UPLOAD_FILES.toLocaleString()} importable files after ignoring dependencies and build output. Groundbeam currently imports up to ${MAX_MANAGED_ASTRO_UPLOAD_FILES.toLocaleString()} files at once; remove generated folders or import from Git so ignore rules can be applied cleanly.`,
        );
      }
    }
  }

  await walk(directory, "", []);
  return { files, skippedFiles };
}

export async function prepareManagedAstroSanityExportUpload(
  file: File,
  existingPaths: readonly string[] = [],
): Promise<PreparedManagedAstroSanityExport> {
  const extension = sanityExportExtension(file.name);
  if (!extension) {
    throw new Error("Choose a Sanity export as a .ndjson or .json file.");
  }
  if (file.size > MAX_SANITY_EXPORT_BYTES) {
    throw new Error(
      `Sanity export is too large. The current limit is ${formatBytes(
        MAX_SANITY_EXPORT_BYTES,
      )}.`,
    );
  }
  const text =
    file.size <= MAX_SANITY_EXPORT_ANALYSIS_TEXT_BYTES
      ? await file.text()
      : null;
  return {
    file,
    mimeType:
      file.type ||
      (extension === "json" ? "application/json" : "application/x-ndjson"),
    originalName: file.name,
    path: uniqueSanityExportPath(extension, existingPaths),
    sizeBytes: file.size,
    text,
  };
}

export function mergeManagedAstroSanityExport(
  prepared: PreparedManagedAstroUpload,
  sanityExport: PreparedManagedAstroSanityExport | null,
): PreparedManagedAstroUpload {
  if (!sanityExport) return prepared;
  const file: ManagedAstroUploadFile = {
    mimeType: sanityExport.mimeType,
    path: sanityExport.path,
    sizeBytes: sanityExport.sizeBytes,
    text: sanityExport.text,
  };
  return {
    ...prepared,
    files: [...prepared.files, file],
    sourceFiles: [
      ...prepared.sourceFiles,
      { file: sanityExport.file, path: sanityExport.path },
    ],
    textBytes:
      prepared.textBytes +
      (sanityExport.text !== null ? sanityExport.sizeBytes : 0),
    totalBytes: prepared.totalBytes + sanityExport.sizeBytes,
    totalFiles: prepared.totalFiles + 1,
  };
}

function sanityExportExtension(name: string): "json" | "ndjson" | null {
  const lower = name.toLowerCase();
  if (lower.endsWith(".ndjson")) return "ndjson";
  if (lower.endsWith(".json")) return "json";
  return null;
}

function uniqueSanityExportPath(
  extension: "json" | "ndjson",
  existingPaths: readonly string[],
): string {
  const existing = new Set(existingPaths);
  for (let i = 1; i < 100; i += 1) {
    const suffix = i === 1 ? "" : `-${i}`;
    const candidate = `.groundbeam/sanity-export${suffix}.${extension}`;
    if (!existing.has(candidate)) return candidate;
  }
  return `.groundbeam/sanity-export-${Date.now()}.${extension}`;
}

function normalizeUploadPath(path: string): string {
  return path
    .replaceAll("\\", "/")
    .replace(/^\.\/+/, "")
    .replace(/^\/+/, "")
    .replace(/\/+/g, "/");
}

function commonTopLevelFolder(paths: string[]): string | null {
  const first = paths[0]?.split("/")[0];
  if (!first) return null;
  return paths.every(
    (path) => path.split("/")[0] === first && path.includes("/"),
  )
    ? first
    : null;
}

function stripTopLevelFolder(path: string, folder: string | null): string {
  if (!folder) return path;
  return path.startsWith(`${folder}/`) ? path.slice(folder.length + 1) : path;
}

async function loadGitignoreRules(
  rawFiles: readonly RawManagedAstroUploadFile[],
  sourceLabel: string | null,
): Promise<GitignoreRule[]> {
  const gitignoreFiles = rawFiles
    .map((entry) => {
      const path = stripTopLevelFolder(entry.path, sourceLabel);
      return { ...entry, path };
    })
    .filter(
      (entry) =>
        entry.path.split("/").at(-1) === ".gitignore" &&
        entry.file.size <= MAX_GITIGNORE_BYTES,
    );
  const rules: GitignoreRule[] = [];
  for (const entry of gitignoreFiles) {
    const text = await entry.file.text();
    rules.push(...parseGitignoreRules(text, pathDirname(entry.path)));
  }
  return rules;
}

function parseGitignoreRules(text: string, baseDir: string): GitignoreRule[] {
  return text
    .split(/\r?\n/)
    .map((line) => parseGitignoreRule(line, baseDir))
    .filter((rule): rule is GitignoreRule => rule !== null);
}

function parseGitignoreRule(
  rawLine: string,
  baseDir: string,
): GitignoreRule | null {
  let line = rawLine.trim();
  if (!line || line.startsWith("#")) return null;
  if (line.startsWith("\\")) line = line.slice(1);
  let negated = false;
  if (line.startsWith("!")) {
    negated = true;
    line = line.slice(1).trim();
  }
  if (!line || line === "/") return null;

  const directoryOnly = line.endsWith("/");
  line = line.replace(/\/+$/g, "");
  const anchored = line.startsWith("/");
  line = line.replace(/^\/+/g, "");
  if (!line) return null;

  const hasSlash = line.includes("/");
  return {
    anchored,
    baseDir,
    directoryOnly,
    hasSlash,
    negated,
    regex: new RegExp(
      `^${gitignoreGlobToRegex(line)}${
        directoryOnly || !hasSlash ? "(?:/.*)?$" : "$"
      }`,
    ),
  };
}

function gitignoreGlobToRegex(pattern: string): string {
  let source = "";
  for (let i = 0; i < pattern.length; i += 1) {
    const char = pattern.charAt(i);
    const next = pattern.charAt(i + 1);
    if (char === "*" && next === "*") {
      source += ".*";
      i += 1;
      continue;
    }
    if (char === "*") {
      source += "[^/]*";
      continue;
    }
    if (char === "?") {
      source += "[^/]";
      continue;
    }
    source += escapeRegExp(char);
  }
  return source;
}

function escapeRegExp(value: string): string {
  return value.replace(/[\\^$.*+?()[\]{}|]/g, "\\$&");
}

function pathDirname(path: string): string {
  const index = path.lastIndexOf("/");
  return index > 0 ? path.slice(0, index) : "";
}

function shouldSkipSourcePath(
  path: string,
  gitignoreRules: readonly GitignoreRule[] = [],
): boolean {
  const parts = path.split("/");
  return (
    parts.some((part) => SKIPPED_SOURCE_DIRS.has(part)) ||
    SKIPPED_SOURCE_FILES.has(parts.at(-1) ?? "") ||
    parts.some((part) => part.startsWith(".env")) ||
    isIgnoredByGitignore(path, gitignoreRules)
  );
}

function shouldSkipSourceDirectoryPath(
  path: string,
  gitignoreRules: readonly GitignoreRule[] = [],
): boolean {
  const parts = path.split("/");
  return (
    parts.some(
      (part) => SKIPPED_SOURCE_DIRS.has(part) || part.startsWith(".env"),
    ) || isIgnoredByGitignore(path, gitignoreRules)
  );
}

function isIgnoredByGitignore(
  path: string,
  rules: readonly GitignoreRule[],
): boolean {
  let ignored = false;
  for (const rule of rules) {
    if (gitignoreRuleMatchesPath(rule, path)) ignored = !rule.negated;
  }
  return ignored;
}

function gitignoreRuleMatchesPath(rule: GitignoreRule, path: string): boolean {
  const relative = pathRelativeToBaseDir(path, rule.baseDir);
  if (!relative) return false;
  if (rule.anchored || rule.hasSlash) return rule.regex.test(relative);
  return relative.split("/").some((part) => rule.regex.test(part));
}

function pathRelativeToBaseDir(path: string, baseDir: string): string | null {
  if (!baseDir) return path;
  return path.startsWith(`${baseDir}/`) ? path.slice(baseDir.length + 1) : null;
}

function shouldReadText(path: string): boolean {
  const filename = path.split("/").at(-1) ?? path;
  return TEXT_FILE_NAMES.has(filename) || TEXT_FILE_RE.test(filename);
}

function isAbortError(err: unknown): boolean {
  return (
    typeof DOMException !== "undefined" &&
    err instanceof DOMException &&
    (err.name === "AbortError" || err.name === "NotAllowedError")
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
